import { useRef } from 'react';
import {
  PropertiesEditorStreamShape,
  PropertyGroup,
  Tags,
} from 'appModelStateEdit/propertiesEditor/types';
import { SidebarMenu, SidebarMenuFormSection } from '@ardoq/sidebar-menu';
import { Editor, TagEditor } from 'scopeData/editors/Editors';
import { PropertyValue } from 'aqTypes';
import {
  modifyProperty,
  modifyTags,
} from 'appModelStateEdit/propertiesEditor/actions';
import { percentageEditValueToStoredValue } from '@ardoq/locale';
import { NUMBER_FORMAT_PERCENTAGE } from '@ardoq/api-types';

import { ComponentStylePreview as ComponentStylePreviewBase } from './componentStylePreview/ComponentStylePreview';
import { omit, kebabCase } from 'lodash';
import { useOnMount } from '@ardoq/hooks';
import { dispatchAction } from '@ardoq/rxbeach';
import { Stack } from '@ardoq/layout';
import { ErrorNotification } from '@ardoq/status-ui';
import { GetContentOptionsType } from 'appModelStateEdit/legacyTypes';
import {
  trackEntityTagsModified,
  trackSelectedPropertyChange,
} from './tracking';
import { t } from '@ardoq/i18n';

type PropertiesEditorProps = {
  readonly onClose: VoidFunction;
  readonly onSave: (propertyGroups: PropertyGroup[], tags: Tags) => void;
  readonly onDelete?: VoidFunction;
  readonly trackingContext: GetContentOptionsType;
};

export const PropertiesEditor = ({
  onClose,
  onSave,
  onDelete,
  errorMessage,
  buttonLabel,
  headerTitle,
  isSaveButtonDisabled,
  lockedMessage,
  multiEditingMessage,
  originalProperties,
  propertyGroups,
  sidebarInstanceId,
  shouldShowComponentStylePreview,
  shouldShowDeleteButton,
  shouldShowSaveButton,
  shouldShowTags,
  tags,
  trackingContext,
  canManageMetamodelConstraints,
  i18nLocale,
  workspaceId,
}: PropertiesEditorProps & PropertiesEditorStreamShape) => {
  const sidebarRef = useRef<HTMLFormElement>(null);
  useOnMount(() => {
    sidebarRef.current?.querySelector('input')?.focus();
  });

  const [firstPropertyGroup, ...otherPropertyGroups] = propertyGroups;
  if (!firstPropertyGroup || firstPropertyGroup.properties.length === 0) {
    return (
      <SidebarMenu
        ref={sidebarRef}
        headerTitle={headerTitle}
        onCloseClick={onClose}
        closeButtonLabel={t(i18nLocale, 'views:rightPane.close', 'Close')}
        closeButtonAriaLabel={t(
          i18nLocale,
          'views:rightPane.closeAriaLabel',
          'Close sidebar'
        )}
      >
        <SidebarMenuFormSection
          titleDataTestId={`${kebabCase(headerTitle)}-sub-header`}
          title={multiEditingMessage}
          isCollapsible={false}
        >
          <>
            {errorMessage && (
              <ErrorNotification>{errorMessage}</ErrorNotification>
            )}
          </>
        </SidebarMenuFormSection>
      </SidebarMenu>
    );
  }
  return (
    <SidebarMenu
      ref={sidebarRef}
      headerTitle={headerTitle}
      onCloseClick={onClose}
      closeButtonLabel={t(i18nLocale, 'views:rightPane.close', 'Close')}
      closeButtonAriaLabel={t(
        i18nLocale,
        'views:rightPane.closeAriaLabel',
        'Close sidebar'
      )}
      footerErrorMessage={errorMessage}
      {...(shouldShowSaveButton
        ? {
            primaryButtonLabel: buttonLabel,
            onPrimaryButtonClick: () => onSave(propertyGroups, tags),
            primaryButtonClickId: 'sidebar-action-save-create',
            isPrimaryButtonDisabled: isSaveButtonDisabled,
          }
        : {})}
      {...(shouldShowDeleteButton
        ? {
            dangerButtonLabel: t(
              i18nLocale,
              'propertiesEditor:buttons.delete',
              'Delete'
            ),
            onDangerButtonClick: onDelete,
            dangerButtonClickId: 'sidebar-action-delete',
          }
        : {})}
    >
      {multiEditingMessage && (
        <SidebarMenuFormSection
          titleDataTestId={`${kebabCase(headerTitle)}-sub-header`}
          title={multiEditingMessage}
          isCollapsible={false}
        />
      )}
      {lockedMessage && (
        <SidebarMenuFormSection title={lockedMessage} isCollapsible={false} />
      )}
      <SidebarMenuFormSection
        titleDataTestId={`${kebabCase(
          firstPropertyGroup.title
        )}-section-header`}
        title={firstPropertyGroup.title}
        isCollapsible={false}
      >
        <Stack gap="medium">
          {firstPropertyGroup.properties.map((property, index) => (
            <Editor
              key={`${sidebarInstanceId}${property.name}${index}`}
              {...omit(property, 'name')}
              onValueChange={(newValue: PropertyValue) => {
                // Convert edit value (25) back to stored value (0.25) for percentages
                const storedValue =
                  property.numberFormatOptions === NUMBER_FORMAT_PERCENTAGE &&
                  typeof newValue === 'number'
                    ? percentageEditValueToStoredValue(newValue)
                    : newValue;

                dispatchAction(
                  modifyProperty({
                    newValue: storedValue,
                    originalProperties,
                    propertyType: property.type,
                    propertyName: property.name,
                  })
                );

                // This is mainly, to avoid tracking the first emit of the description property.
                // Since the RichEditor emits a change event when the editor is mounted
                if (property.value === newValue) {
                  return;
                }
                trackSelectedPropertyChange(false, property, trackingContext);
              }}
              dataClickId={`editor-input-${property.name}`}
              canManageMetamodelConstraints={canManageMetamodelConstraints}
              i18nLocale={i18nLocale}
              workspaceId={workspaceId}
            />
          ))}
        </Stack>
      </SidebarMenuFormSection>
      {shouldShowTags && (
        <SidebarMenuFormSection isCollapsible={false}>
          <TagEditor
            isDisabled={tags.isDisabled}
            label={t(i18nLocale, 'propertiesEditor:groups.tags', 'Tags')}
            value={tags.value}
            options={tags.options}
            onValueChange={value => {
              dispatchAction(modifyTags(value as string[]));
              trackEntityTagsModified(trackingContext);
            }}
            i18nLocale={i18nLocale}
          />
        </SidebarMenuFormSection>
      )}
      {otherPropertyGroups
        .filter(({ properties }) => properties.length)
        .map((propertyGroup, groupIndex) => (
          <SidebarMenuFormSection
            key={`${sidebarInstanceId}group${groupIndex}`}
            title={propertyGroup.title}
            isCollapsible={false}
          >
            {propertyGroup.properties.map((property, index) => (
              <Editor
                key={`${sidebarInstanceId}${property.name}${index}`}
                {...omit(property, 'name')}
                onValueChange={(newValue: PropertyValue) => {
                  // Convert edit value (25) back to stored value (0.25) for percentages
                  const storedValue =
                    property.numberFormatOptions === NUMBER_FORMAT_PERCENTAGE &&
                    typeof newValue === 'number'
                      ? percentageEditValueToStoredValue(newValue)
                      : newValue;

                  dispatchAction(
                    modifyProperty({
                      newValue: storedValue,
                      originalProperties,
                      propertyType: property.type,
                      propertyName: property.name,
                    })
                  );
                  trackSelectedPropertyChange(true, property, trackingContext);
                }}
                canManageMetamodelConstraints={canManageMetamodelConstraints}
                i18nLocale={i18nLocale}
                workspaceId={workspaceId}
              />
            ))}
          </SidebarMenuFormSection>
        ))}

      {shouldShowComponentStylePreview && (
        <SidebarMenuFormSection
          isCollapsible={false}
          titleDataTestId="preview-section-header"
        >
          <ComponentStylePreviewBase />
        </SidebarMenuFormSection>
      )}
    </SidebarMenu>
  );
};
